import React, { useEffect, useState } from "react";
import { coerceForSpec, type PropertySpec, type OptionItem } from "../../../hooks/useDataTypes";

interface Props {
    spec: PropertySpec;
    value: any;
    context: Record<string, any>;
    onChange: (v: any, label?: string) => void;
    getOptions: (source: string) => Promise<OptionItem[]>;
}


export const DynamicField: React.FC<Props> = ({ spec, value, context, onChange, getOptions }) => {
    const [dynamicOpts, setDynamicOpts] = useState<OptionItem[] | null>(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        let active = true;

        (async () => {
            if (spec.control === "dropdown" && spec.options_source) {
                const base = spec.options_source;
                const depKey = spec.depends_on;


                // 🚨 Check if depKey is required and not yet selected
                if (typeof depKey === 'string' && !context?.[depKey]) {
                    setDynamicOpts(null);
                    return;
                }

                // ✅ Build final source safely
                const finalSource = typeof depKey === 'string' ? `${base}/${context[depKey]}` : base;

                setLoading(true);
                try {
                    const opts = await getOptions(finalSource);
                    if (!active) return;
                    setDynamicOpts(opts);

                    const defaultValue = spec.default;

                    // Check if current value is missing
                    const currentValueExists = opts.some((o) => String(o.value) === String(value));
                    const defaultValueExists = opts.some((o) => String(o.value) === String(defaultValue));

                    if (!currentValueExists) {
                        if (defaultValue !== undefined && defaultValueExists) {
                            onChange(coerceForSpec(spec, defaultValue));
                        } else if (opts.length > 0) {
                            onChange(coerceForSpec(spec, opts[0].value));
                        }
                    }

                } finally {
                    if (active) setLoading(false);
                }
            } else {
                setDynamicOpts(null);
            }
        })();

        return () => {
            active = false;
        };
    }, [spec.options_source, typeof spec.depends_on === 'string' ? context?.[spec.depends_on] : null]);


    const FieldLabel = ({ spec }: { spec: PropertySpec }) => (
        <label
            htmlFor={spec.key}
            className="font-medium"
            style={{ minWidth: '67px', fontSize: '0.8rem', flexShrink: 0 }}
            title={spec.tooltip ?? undefined}
        >
            {spec.label}
            {spec.tooltip && (
                <span style={{ marginLeft: 4, color: "var(--text-muted)", cursor: "help" }}>🛈</span>
            )}
        </label>
    );



    const wrapperStyle: React.CSSProperties = {
        display: 'flex',
        alignItems: 'center',
        marginBottom: 10,
        gap: '0.5rem'
    };

    if (spec.control === "dropdown") {
        const opts: OptionItem[] = dynamicOpts ?? (spec.options ?? []);


        return (
            <div style={wrapperStyle} onMouseDown={(e) => e.stopPropagation()}>
                <FieldLabel spec={spec} />
                <div onMouseDown={(e) => e.stopPropagation()}>
                    <select
                        id={spec.key}
                        value={value ?? ""}
                        onChange={(e) => {
                            const val = coerceForSpec(spec, e.target.value);
                            const lbl = e.target.options[e.target.selectedIndex]?.text || String(val);
                            onChange(val, lbl);
                        }}
                        disabled={(!!spec.options_source && dynamicOpts === null) || loading}
                        style={{
                            width: '120px',
                            padding: '0.4rem',
                            background: 'var(--bg-app)',
                            color: 'var(--text-primary)',
                            border: '1px solid var(--surface-glass-border)',
                            borderRadius: 'var(--radius-sm)',
                            outline: 'none'
                        }}
                    >
                        <option value="">
                            {loading ? "Loading…" : "Select…"}
                        </option>
                        {opts.map((o) => (
                            <option key={String(o.value)} value={String(o.value)}>
                                {o.label}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
        );
    }

    if (spec.control === "date") {
        return (
            <div style={wrapperStyle} onMouseDown={(e) => e.stopPropagation()}>
                <FieldLabel spec={spec} />

                <div onPointerDown={(e) => e.stopPropagation()}>
                    <input
                        id={spec.key}
                        type="date"
                        value={value ?? ""}
                        onChange={(e) => onChange(e.target.value, e.target.value)}
                        style={{
                            background: 'var(--bg-app)',
                            color: 'var(--text-primary)',
                            border: '1px solid var(--surface-glass-border)',
                            borderRadius: 'var(--radius-sm)',
                            padding: '0.4rem',
                            outline: 'none'
                        }}
                    />
                </div>
            </div>
        );
    }

    if (spec.control === "number") {
        return (
            <div style={wrapperStyle} onMouseDown={(e) => e.stopPropagation()}>
                <FieldLabel spec={spec} />

                <div onPointerDown={(e) => e.stopPropagation()}>
                    <input
                        id={spec.key}
                        type="text"
                        inputMode="decimal"
                        value={value ?? ""}
                        onChange={(e) => onChange(e.target.value, e.target.value)}
                        onBlur={(e) => onChange(coerceForSpec(spec, e.target.value))}
                        style={{
                            width: '100px',
                            padding: '0.4rem',
                            background: 'var(--bg-app)',
                            color: 'var(--text-primary)',
                            border: '1px solid var(--surface-glass-border)',
                            borderRadius: 'var(--radius-sm)',
                            outline: 'none'
                        }}
                    />
                </div>
            </div >
        );
    }

    if (spec.control === "text") {
        return (
            <div style={wrapperStyle} onMouseDown={(e) => e.stopPropagation()}>
                <FieldLabel spec={spec} />

                <div onPointerDown={(e) => e.stopPropagation()}>
                    <input
                        id={spec.key}
                        type="text"
                        value={value ?? ""}
                        onChange={(e) => onChange(e.target.value, e.target.value)}
                        style={{
                            background: 'var(--bg-app)',
                            color: 'var(--text-primary)',
                            border: '1px solid var(--surface-glass-border)',
                            borderRadius: 'var(--radius-sm)',
                            padding: '0.4rem',
                            outline: 'none'
                        }}
                    />
                </div>
            </div>
        );
    }

    if (spec.control === "checkbox") {
        return (
            <div className="flex items-center gap-2 mb-2" onMouseDown={(e) => e.stopPropagation()}>
                <label className="flex items-center gap-2">
                    <div onPointerDown={(e) => e.stopPropagation()}>
                        <input
                            type="checkbox"
                            checked={!!value}
                            onChange={(e) => onChange(e.target.checked, e.target.checked ? "Yes" : "No")}
                        />
                    </div>
                    {spec.label}
                </label>
            </div>
        );
    }

    return null;
};


